/**
 * GettingStartedDrawer.jsx — v3.7.15 Phase 84.32 Part 1
 *
 * Full checklist inside a right-side overlay (desktop) / bottom sheet
 * (mobile). Deep-links every step, marks completed steps with a strike,
 * and highlights the single recommended next step per the spec.
 *
 * Reuses the existing shadcn `Sheet` component so we inherit focus trap,
 * ESC-to-close and correct ARIA semantics.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Circle, ArrowRight, PartyPopper } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { computeChecklistProgress, GETTING_STARTED_ACK_KEY } from '@/components/dashboard/GettingStartedPill';

const STEPS = [
  { key: 'account',      title: 'Add an account',
    description: 'Connect a bank, wallet or credit card so 2120 can see the basics.',
    cta: 'Add an account',       href: '/accounts?tab=cash' },
  { key: 'import',       title: 'Import a statement',
    description: 'Bring in a few months of history to unlock trends and insights.',
    cta: 'Import a statement',   href: '/import' },
  { key: 'categorise',   title: 'Review uncertain categorisation',
    description: 'Confirm 2120 has the right categories for your recent activity.',
    cta: 'Review categorisation', href: '/transactions?filter=uncategorised' },
  { key: 'priority',     title: 'Choose your primary financial priority',
    description: 'One priority shapes what your dashboard emphasises first.',
    cta: 'Pick a priority',       href: '/profile?tab=life' },
  { key: 'first_action', title: 'Take your first guided action',
    description: 'Review three recurring commitments that may be slowing progress.',
    cta: 'Open recurring',        href: '/recurring' },
];

export default function GettingStartedDrawer({ open, onOpenChange, activation }) {
  const nav = useNavigate();
  const { steps, done, total, complete } = React.useMemo(
    () => computeChecklistProgress(activation),
    [activation],
  );

  // Show a celebratory toast the first time the user opens the drawer
  // in a complete state, then acknowledge so the pill retires.
  useEffect(() => {
    if (!open || !complete) return;
    try {
      const already = window.localStorage.getItem(GETTING_STARTED_ACK_KEY);
      if (!already) {
        toast.success('You’re all set up. Nice work.', { icon: '🎉', duration: 5000 });
        window.localStorage.setItem(GETTING_STARTED_ACK_KEY, new Date().toISOString());
      }
    } catch { /* private mode */ }
  }, [open, complete]);

  const nextStepIdx = steps.findIndex((s) => !s.done);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md flex flex-col"
        data-testid="getting-started-drawer"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-display text-xl">
            Getting started · {done} of {total}
          </SheetTitle>
          <SheetDescription>
            {complete
              ? 'Every step is done. You can find the tour again anytime under Help.'
              : nextStepIdx >= 0
                ? 'One last step to make 2120 yours.'
                : 'Bring 2120 to life in a few short steps.'}
          </SheetDescription>
        </SheetHeader>

        {complete ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-4">
            <PartyPopper className="h-10 w-10 text-emerald-600 mb-3" aria-hidden="true" />
            <div className="text-lg font-medium text-charcoal">All done.</div>
            <div className="text-sm text-muted-foreground mt-2 max-w-xs">
              Come back when life changes — new job, new city, new plan — and 2120 will adjust.
            </div>
          </div>
        ) : (
          <ol className="flex-1 overflow-y-auto space-y-2 mt-4 pr-1" aria-label="Onboarding steps">
            {STEPS.map((s, idx) => {
              const isNext = idx === nextStepIdx;
              const isDone = steps[idx]?.done;
              const Icon = isDone ? CheckCircle2 : Circle;
              return (
                <li
                  key={s.key}
                  className={
                    'rounded-xl border px-3 py-3 transition-colors ' +
                    (isNext
                      ? 'border-primary/40 bg-amber-50/60 dark:bg-amber-950/20'
                      : 'border-border/60 bg-card')
                  }
                  data-testid={`getting-started-step-${s.key}`}
                >
                  <div className="flex items-start gap-3">
                    <Icon
                      className={
                        'h-4 w-4 mt-0.5 shrink-0 ' +
                        (isDone ? 'text-emerald-600' : isNext ? 'text-primary' : 'text-muted-foreground')
                      }
                      aria-hidden="true"
                    />
                    <div className="min-w-0 flex-1">
                      <div className={
                        'text-sm font-medium ' +
                        (isDone ? 'text-muted-foreground line-through' : 'text-charcoal')
                      }>
                        {idx + 1}. {s.title}
                      </div>
                      {!isDone && (
                        <div className="text-[12px] text-muted-foreground mt-0.5 leading-relaxed">
                          {s.description}
                        </div>
                      )}
                      {isNext && (
                        <Button
                          size="sm"
                          className="mt-2 rounded-full"
                          onClick={() => { onOpenChange(false); nav(s.href); }}
                          data-testid={`getting-started-cta-${s.key}`}
                        >
                          {s.cta} <ArrowRight className="h-3.5 w-3.5 ml-1" aria-hidden="true" />
                        </Button>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </SheetContent>
    </Sheet>
  );
}
